import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  Tooltip,
  Typography
} from '@mui/material'
import {
  Download as DownloadIcon,
  FolderOpen as FolderIcon
} from '@mui/icons-material'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { Status } from '../../../hooks/useFetchState'
import { Loading } from '../../../components/UI'
import React, { Fragment, useState } from 'react'
import { ProductOriginsDDL } from '../../../components/Producto/ProductOrigins/ProductOriginsDDL'
import { useApi } from '../../../hooks/useApi'
import {
  getUpdatePreSaleSkuTemplate,
  postUpdatePreSaleSkuMasiveFile
} from '../api'

interface EditPreSaleSkuMasiveModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestWorkOrderRecall: (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => void
}

export const EditPreSaleSkuMasiveModal: React.FC<
  EditPreSaleSkuMasiveModalProps
> = ({ handleSnackbar, handleCloseModal, requestWorkOrderRecall }) => {
  const [currentFile] = useState<any | undefined>(undefined)
  const [progress, setProgress] = useState<number>(0)
  const [checkIsInfiniteQty, setCheckIsInfiniteQty] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<any[] | undefined>(
    undefined
  )
  const [selectedProductOrigins, setSelectedProductOrigins] =
    useState<number>(0)
  const { state: requestLabelState, callApi: requestDownloadTemplate } =
    useApi()
  const { state, callApi } = useApi()

  const handleSetProductOrigin = (id: number) => {
    setSelectedProductOrigins(id)
  }

  const handleSelectFile = (event: any) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const handleChkQuality = () => {
    setCheckIsInfiniteQty(!checkIsInfiniteQty)
  }

  const validateButtonLoad = () => {
    if (!selectedFiles || selectedProductOrigins <= 0) return true
    else return false
  }

  const handleSubmit = () => {
    const formData = new FormData()

    // Agregar los valores del formulario
    formData.append('brandId', `${selectedProductOrigins}`)
    formData.append('isInfiniteQty', `${checkIsInfiniteQty}`)

    // Agregar el archivo seleccionado
    if (selectedFiles && selectedFiles.length > 0)
      formData.append('file', selectedFiles[0])

    callApi(postUpdatePreSaleSkuMasiveFile(formData))
      .then(() => {
        handleSnackbar('Carga realizada exitosamente', true)
        requestWorkOrderRecall()
        handleCloseModal()
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  const handleDownloadTemplate = () => {
    requestDownloadTemplate(getUpdatePreSaleSkuTemplate())
      .then((response) => {
        const raw = window.atob(response.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
  }

  if (
    state.status === Status.Loading ||
    requestLabelState.status === Status.Loading
  ) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  } else {
    return (
      <Fragment>
        <Typography variant='subtitle2' sx={{ mb: 3, fontWeight: 500 }}>
          Complete los datos para realizar la carga
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <ProductOriginsDDL
              onChange={(e: any) =>
                handleSetProductOrigin(e.target.value as number)
              }
              value={selectedProductOrigins}
              required={true}
              type='outlined'
              size='small'
              fullWidth
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size='medium'
                  color='primary'
                  onChange={() => handleChkQuality()}
                />
              }
              label='Stock Infinito'
            />
          </Grid>
        </Grid>

        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
          Puede descargar una plantilla desde el link que se encuentra
          disponible a continuación.
        </Typography>

        <Button
          variant='outlined'
          color='primary'
          onClick={handleDownloadTemplate}
          startIcon={<DownloadIcon />}
          sx={{ mb: 3, borderRadius: '10px', textTransform: 'none' }}
        >
          Plantilla
        </Button>
        <Divider />
        <Box
          sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, mt: 2 }}
        >
          <label htmlFor='btn-upload' style={{ flex: '0 0 auto' }}>
            <input
              id='btn-upload'
              name='btn-upload'
              style={{ display: 'none' }}
              type='file'
              onChange={handleSelectFile}
              accept='.xls,.xlsx'
            />
            <Button
              variant='outlined'
              color='primary'
              component='span'
              startIcon={<FolderIcon />}
              sx={{ borderRadius: '10px', textTransform: 'none' }}
            >
              Seleccionar archivo
            </Button>
          </label>

          <Typography
            variant='caption'
            sx={{ flex: 1, color: 'text.secondary' }}
            noWrap
          >
            {selectedFiles && selectedFiles.length > 0
              ? selectedFiles[0].name
              : 'Ningún archivo seleccionado'}
          </Typography>
        </Box>

        {currentFile && (
          <Box
            style={{
              margin: '2px',
              visibility: currentFile ? 'visible' : 'hidden',
              marginBottom: '24px'
            }}
          >
            <LinearProgress
              variant='determinate'
              value={progress}
              sx={{
                height: 15,
                borderRadius: '5px',
                backgroundColor: '#EEEEEE',
                '& .MuiLinearProgress-bar': {
                  borderRadius: '5px',
                  backgroundColor: '#1a90ff'
                }
              }}
            />

            <Box minWidth={35}>
              <Typography
                variant='body1'
                color='textSecondary'
                style={{ textAlign: 'end' }}
              >{`${progress}%`}</Typography>
            </Box>
          </Box>
        )}
        <Divider />
        <Box
          sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}
        >
          <Tooltip
            title='Enviar el archivo seleccionado al servidor para su procesamiento'
            placement='top'
          >
            <span style={{ display: 'inline-block' }}>
              <Button
                color='primary'
                variant='contained'
                disabled={validateButtonLoad()}
                onClick={handleSubmit}
              >
                Aceptar
              </Button>
            </span>
          </Tooltip>
          <Button variant='outlined' color='primary' onClick={handleCloseModal}>
            Cancelar
          </Button>
        </Box>
      </Fragment>
    )
  }
}
